import {AbstractMainPlugin} from "../../AbstractMainPlugin";
import {Item} from "../../../models/Item";
import {ObjectArrayComponent} from "./object-array-component/object-array.component";
import {ItemManagerService} from "../../../services/itemManager/item-manager.service";
import {appInjector} from "../../appInjector";
import {debounceTime, delay, filter, skipWhile, take, takeUntil} from 'rxjs/operators';
import {ItemConfig} from "../../../models/ItemConfig";
import {UiAdapterService} from "../../../services/uiAdapterService/ui-adapter.service";
import {CesiumAdapterService} from "../../../services/cesiumAdapter21/cesium-adapter.service";
import {MapPosition} from "../../../models/MapPosition";
import {ObjectArrayService} from "./object-array-service/ObjectArrayService";
import {iif, ReplaySubject, Subject, Subscription, timer} from 'rxjs';
import {MultiSelectItemsService} from "../../../services/multiSelectItemsService/multi-select-items.service";
import {EsBsModalService} from "../../../services/bsModalService/bs-modal.service";
import {Cartesian2} from 'cesium';
import {GroupService} from '@src/app/services/groupService/group-service.service';

export class ObjectArrayPlugin extends AbstractMainPlugin {
  private im: ItemManagerService;
  private gs: GroupService;
  private ui: UiAdapterService;
  private ms: MultiSelectItemsService;
  private bs: EsBsModalService;
  private cesiumAdapter: CesiumAdapterService;
  private objectArrService: ObjectArrayService;
  private oldConfig = null;
  private oldConfigHash = null;
  private currentParent:Item = null;
  // private isListenersInitialized:boolean = false;
  private isLoaded$ = new ReplaySubject(1);
  private draggableCopies = null;
  private draggableItems = null;
  private childCounterByDirection:string;
  private childSubscriptions = new Subscription();
  private parentSubscriptions = null;
  private _updatePositionSubject$ = new Subject<boolean>();
  private isReloadComponent = false;

  constructor(item:Item) {
    super(item);
    this.im = appInjector.get(ItemManagerService);
    this.gs = appInjector.get(GroupService);
    this.ui = appInjector.get(UiAdapterService);
    this.cesiumAdapter = appInjector.get(CesiumAdapterService);
    this.objectArrService = appInjector.get(ObjectArrayService);
    this.ms = appInjector.get(MultiSelectItemsService);
    this.bs = appInjector.get(EsBsModalService);
    item.emitter.pipe(takeUntil(this.isLoaded$)).subscribe((event) => {
      if (event.getEventName() === 'addedToItemManager') {
        this.initializeListeners();
      }
    });

    this.cesiumAdapter.onMouseMovedDifference$.pipe(filter((val)=>{
      return this.currentParent && this.item.getId() == this.currentParent.getId() && (this.ms.currentDraggableItemId == this.item.getId() || (Object.keys(this.im.selectedItemList).length > 1 && this.im.selectedItemList.hasOwnProperty(this.currentParent.getId())))
    })).subscribe((offset)=>{
      this.draggableItems = this.getCopiesFlatList();
      this.draggableItems.unshift(this.item);
      this.draggableItems.forEach((i:Item)=>{
          i.getObservable(Item.MOVE_OBJECT_BY_OFFSET).next(offset);
        })
      });

    this.cesiumAdapter.dragEnd.pipe(filter(({position, id})=>{
      return this.currentParent && this.item.getId() == this.currentParent.getId()
    })).subscribe(({position, id})=>{
      let copies = this.getCopiesFlatList();
      copies.forEach((i)=>{
        i.getObservable('updateMultiSelectDragPosition').next(true);
      });
      this.draggableCopies = null;
    })

    this.item.getSubject('UPDATE_COPY_ID').pipe(filter(val => this.item.config.isCopy)).subscribe(()=>{
      let oldVal = this.item.config.isCopy;
      this.item.config.copyParent = this.objectArrService.undoItemsList[oldVal];
      timer(1000).subscribe(()=>{
        if (this.objectArrService.undoItemsList[oldVal]){
          delete this.objectArrService.undoItemsList[oldVal];
        }
      });
      this.initializeListeners();
    })
  }

  private isCurrentParentContainChangedField(){
    return this.currentParent.config.getInputProp('__objectArray', 'is_changed');
  }

  static getComponentForLocation(name, item?){
    if (item && (ObjectArrayPlugin.isItemHasObjectArray(item) || item.config.isCopy) && name == 'location1'){
      return ObjectArrayComponent;
    } else if (!name) {
      return [ObjectArrayComponent];
    }
    return null;
  }

  static isItemHasObjectArray(item:Item):boolean{
    return !item.config.isCopy && item.config.getInputProp('__objectArray', 'value')
  }

  private updateCopyPositionInParentItem(pos:{x,y}){
    if (this.isCurrentParentContainChangedField()){
      return;
    }
    let copies = this.currentParent.config.copies;
    if (!copies[pos.y]){
      copies[pos.y] = [];
    }
    copies[pos.y][pos.x] = this.item.getId();
    this.currentParent.config.copies = copies;
  }

  private initializeListeners(){

    this.item.getSubject(Item.OBJECT_CLICK).pipe(filter(()=> this.item.config.isCopy)).pipe(debounceTime(100)).subscribe(()=>{
      this.item.getObservable('highlight_object').next(true);
    });

    if(ObjectArrayPlugin.isItemHasObjectArray(this.item)){
      this.currentParent = this.item;
      this.initializeParentListeners();
    }

    this.initializeAdditionalChildListeners();

    this.initializeChildListeners();

    this.item.getObservable(Item.SET_NEW_PARENT_FOR_OBJECT_ARRAY_COPY).subscribe((_parentId)=>{

      let _parentItem = null;
      if (_parentId!=null){
        _parentItem = this.im.getItemById(_parentId);
      }

      if (this.item.config.isCopy && this.currentParent && this.isCurrentParentContainChangedField()) {
        this.currentParent.decrementCounter(ObjectArrayComponent.OBJECT_ARRAY_COUNTER_X);
        this.currentParent = _parentItem;
      }
      this.item.config.copyParent = _parentId;
      this.item.config.copyDirection = 'x';
    })

    this.item.getObservable('RELOAD_COMPONENT').subscribe(()=>{
      this.isReloadComponent = true;
        this.initializeParentListeners();

        this.getCopiesFlatList().forEach((i)=>{
          i.getObservable('RELOAD_CHILD').next(true);
        })
    });

    this.item.getObservable('RELOAD_CHILD').subscribe(()=>{
      if (this.childSubscriptions){
        this.childSubscriptions.unsubscribe();
        this.childSubscriptions = new Subscription();
      }
      if (this.item.config.isCopy) {
        this.currentParent = this.im.getItemById(this.item.config.getCopyParentId);
        this.childCounterByDirection = this.item.config.copyDirection.toLowerCase() === 'y' ? ObjectArrayComponent.OBJECT_ARRAY_COUNTER_Y : (this.item.config.copyDirection.toLowerCase() === 'x') ? ObjectArrayComponent.OBJECT_ARRAY_COUNTER_X: null;
        this.childSubscriptions.add(this.item.getSubject(Item.OBJECT_CLICK).pipe(delay(10)).subscribe(() => {
          // let parentItem = this.im.getItemById(this.item.config.getCopyParentId);
          // if (this.item.config.isCopy) {
          //   this.ui.selectedItem$ = parentItem;
          // }
        }));
        this.childSubscriptions.add(this.item.getObservable(Item.APPLY_NEW_TOOL).subscribe((tool)=>{
          this.setNewConfigToItem(tool);
        }));
      }
    })

    this.cesiumAdapter.onEmptyClick$.subscribe(()=>{
      // if (this.currentParent) {
        this.item.getObservable('highlight_object').next(false);
      // }
    });




    //todo: add check for parentItem and
    //add methods for parent object

  }

  private initializeParentListeners(){
    if(!ObjectArrayPlugin.isItemHasObjectArray(this.item)) {
      return;
    }

    if (this.parentSubscriptions){
      this.parentSubscriptions.unsubscribe();
    }
    this.parentSubscriptions = new Subscription();

    this.parentSubscriptions.add(this._updatePositionSubject$.pipe(debounceTime(500)).subscribe(()=>{
      this.getCopiesFlatList().forEach((i)=>{
        i.getObservable('updateMultiSelectDragPosition').next(true);
      })
    }))

    this.parentSubscriptions.add(this.item.getSubject(Item.SUBJECT_INPUTS_CHANGED).subscribe((method)=>{
      let copies = this.im.getAllItemCopies(this.item);
      copies.forEach((i:Item)=>{
        i.getObservable(Item.EXECUTE_STATIC_INPUTS_METHOD).next(method);
      })
    }))

    this.currentParent = this.item;

    if (this.item.getId()){
      this.item.config.copies = [[this.item.getId()]];
    }

    this.parentSubscriptions.add(this.item.getObservable('REMOVE_OBJECT_ARRAY_PARENT_LISTENERS').subscribe(()=>{
      if (this.parentSubscriptions){
        this.parentSubscriptions.unsubscribe();
      }
    }))

    //move copies in one go during drag end for objects array that created by multiselect tool "is_changed: true"
    this.parentSubscriptions.add(this.item.getObservable('objectArrayDragEnd').pipe(filter(({item, object}) => {
      return this.currentParent && item.getId() == this.currentParent.getId() && !this.im.isInSelectedItems(item)})).subscribe(({item, object})=>{
      if (this.isCurrentParentContainChangedField()){
        let newPosition = new MapPosition(object.object.position);
        let oldPosition = new MapPosition(object.oldPosition);
        let offset = {latitude: newPosition.lat-oldPosition.lat, longitude: newPosition.lng-oldPosition.lng};
        let copies = this.getCopiesFlatList();
        copies.forEach((i)=>{
          i.getObservable(Item.MOVE_OBJECT_BY_OFFSET).next(offset);
          this._updatePositionSubject$.next(true);
          // timer(500).subscribe(()=>{
          //   i.getObservable('updateMultiSelectDragPosition').next(true);
          // })
        })
        return;
      }
    }))

    this.parentSubscriptions.add(this.item.emitter.subscribe((e)=> {
      if (e.getEventName() === 'addedToItemManager') {
        this.item.config.copies = [[this.item.getId()]];
      }
      if (e.getEventName() == 'removeObject' && !e.withoutMessage) {
        if (!e.getIsGroupListener()) {
          var actualCopies = this.getCopiesFlatList();
          actualCopies.forEach((_item: Item) => {
            _item.removeObject();
          })
        }
      }
    })
    );

    this.parentSubscriptions.add(this.ui.selectedItem$.pipe(filter((i)=>{return i instanceof Item})).subscribe((item:Item)=>{
      if (item.getId() != this.item.getId()){
        this.currentParent.getObservable('highlight_object').next(false);
      }
    })
    );

    this.parentSubscriptions.add(this.item.config.serverUpdate.subscribe((config: ItemConfig)=>{
      this.processServerItemUpdate(config);
    })
    );

    this.parentSubscriptions.add(this.item.config.subject.pipe(debounceTime(1000)).subscribe((config: ItemConfig)=>{
      this.processInternalItemUpdate(config);
    })
    );

    this.parentSubscriptions.add(this.item.getSubject(ObjectArrayComponent.OBJECT_ARRAY_COUNTER_SUBJECT).pipe(debounceTime(500)).subscribe((val:{x,y})=>{
      this.updateCopies(val);
    })
    );

    this.parentSubscriptions.add(this.item.getSubject(Item.SUBJECT_ROTATE_BY).pipe(skipWhile(v => this.isReloadComponent)).subscribe((val)=>{

      if (this.isCurrentParentContainChangedField()) {
        let {newVal} = val;
        this.rotateGroupedItems(newVal)
        return;
      }

        var actualCopies = this.getCopiesFlatList();
        actualCopies.forEach((_item:Item)=>{
          _item.getSubject(Item.SUBJECT_ROTATE_BY).next(val);
        });
      // this.updateCopies();
    })
    );

    this.parentSubscriptions.add(this.item.getSubject(Item.SUBJECT_VISIBILITY).subscribe((visibilityFlag)=>{
      var actualCopies = this.getCopiesFlatList();
      actualCopies.forEach((_item:Item)=>{
        _item.setVisibilityComponent(this.constructor.name, visibilityFlag);
      });
    })
    )

    this.parentSubscriptions.add(this.item.getSubject('toolSwitched').subscribe((newTool)=>{
      this.currentParent.config.setInputValue('__objectArray', 'is_changed', true);
      // let _confirm = confirm(Yiit.t('Should the new tool be applied to all copies?'));
      // if (_confirm){
        let copies = this.im.getAllItemCopies(this.item);
        copies.forEach((i:Item)=>{
          i.getObservable(Item.APPLY_NEW_TOOL).next(newTool);
        })
      // }
    })
    );

    this.parentSubscriptions.add(this.item.getObservable('ungroup').pipe(debounceTime(200)).subscribe(()=>{
      if (this.parentSubscriptions){
        this.parentSubscriptions.unsubscribe();
      }
      let copies = this.getCopiesFlatList();
      copies.forEach((copy:Item)=>{
        copy.config.removeCopyOf();
        copy.setMainSidebarItemVisibility(true);
        copy.config.removeInput('__positionInCopies');
        if (this.ui.globalDragEnabled){
          copy.drag_enabled = true;
        }

        if (!copy.config.getTool().params['fixedHeight'] && copy.config.getInputByName('fixedHeight')){
          copy.config.removeInput('fixedHeight');
        }
        if (!copy.config.getTool().params['fixedWidth'] && copy.config.getInputByName('fixedWidth')){
          copy.config.removeInput('fixedWidth');
        }
      });
      if (this.getCopiesFlatList().length === 0){
          this.item.config.copies = null;
      }
      this.item.config.removeCopyOf();
      this.item.config.removeInput('__objectArray');
      // if (!this.item.config.getTool().params['fixedHeight'] && this.item.config.getInputByName('fixedHeight')){
      //   this.item.config.removeInput('fixedHeight');
      // }
      // if (!this.item.config.getTool().params['fixedWidth'] && this.item.config.getInputByName('fixedWidth')){
      //   this.item.config.removeInput('fixedWidth');
      // }
      this.currentParent.clearCounter(ObjectArrayComponent.OBJECT_ARRAY_COUNTER_X);
      this.currentParent.clearCounter(ObjectArrayComponent.OBJECT_ARRAY_COUNTER_Y);
      this.currentParent.getSubject(ObjectArrayComponent.OBJECT_ARRAY_COUNTER_SUBJECT).next({x: 0, y: 0})
      timer(200).subscribe(()=>{
        this.item.getObservable('RELOAD_COMPONENT').next(true);
        this.item.setMainSidebarItemVisibility(true);
      });
    })
    );

    // this.item.getObservable(Item.OBJECT_OFFSET_WITH_DIRECTION).subscribe((event)=>{
    //   if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)){
    //     let copies = this.getCopies();
    //     copies.forEach((copy:Item)=>{
    //       copy.mapObject.config.removeCopyOf();
    //       copy.setMainSidebarItemVisibility(true);
    //     });
    //     self.mapObject.moveWithDirection(event);
    //   }
    // });
    this.getCopiesFlatList().forEach((copy)=>{
      copy.drag_enabled = false;
    });
    this.isReloadComponent = false;
  }

  private initializeChildListeners(){
    if (this.item.config.isCopy){
      let parentId = this.item.config.getCopyParentId;
      let parentItem: Item = this.im.getItemById(parentId);

      if (!parentItem){
        timer(1000).subscribe(()=>{
          this.initializeChildListeners();
        });
        return;
      }
      this.item.setMainSidebarItemVisibility(false);
      this.currentParent = parentItem;


      this.childCounterByDirection = this.item.config.copyDirection.toLowerCase() === 'y' ? ObjectArrayComponent.OBJECT_ARRAY_COUNTER_Y : (this.item.config.copyDirection.toLowerCase() === 'x') ? ObjectArrayComponent.OBJECT_ARRAY_COUNTER_X: null;

      if (this.isCurrentParentContainChangedField()){
        this.childCounterByDirection = ObjectArrayComponent.OBJECT_ARRAY_COUNTER_X;
      }

      if (this.childCounterByDirection){
        this.currentParent.incrementCounter(this.childCounterByDirection);
      }

      this.childSubscriptions.add(this.item.getSubject(Item.OBJECT_CLICK).pipe(delay(10)).subscribe(()=>{
        // if (this.item.config.isCopy){
        //   this.ui.selectedItem$ = this.currentParent;
        // }
      }));

      if (this.item.getId()){
        let positionCopies = this.item.config.getInputProp('__positionInCopies', 'value');
        positionCopies = JSON.parse(positionCopies);
        this.updateCopyPositionInParentItem(positionCopies);
      }

      this.childSubscriptions.add(this.item.emitter.subscribe((e)=>{
        if (e.getEventName() == 'removeObject'){
          if (this.childCounterByDirection){
            this.currentParent.decrementCounter(this.childCounterByDirection);
          }

          if(e.isSelectedRemove && this.item.config.isCopy){
            this.currentParent.removeObject();
          }
        }

        if (e.getEventName() === 'addedToItemManager') {
          let positionCopies = this.item.config.getInputProp('__positionInCopies', 'value');
          positionCopies = JSON.parse(positionCopies);
          this.updateCopyPositionInParentItem(positionCopies);
        }
      }));

      this.childSubscriptions.add(this.item.getSubject('toolSwitched').subscribe((newTool)=>{
        this.currentParent.config.setInputValue('__objectArray', 'is_changed', true);
      }));

      this.childSubscriptions.add(this.item.getObservable(Item.APPLY_NEW_TOOL).subscribe((tool)=>{
        this.setNewConfigToItem(tool);
      }));

      this.childSubscriptions.add(this.item.getObservable('ungroup').subscribe(()=>{
        this.currentParent.getObservable('ungroup').next(true);
      }));

      this.isLoaded$.next(1);


    }

  }

  private setNewConfigToItem(tool){
    let newTool = Object.assign({}, tool);
    if (this.item.config.getTool().name != this.item.config.getTitle()){
      newTool.name = this.item.config.getTitle();
    }
    let object = {
      tool: newTool,
      position: this.getItemPositionByType(),
      rotation: this.item.config.getRotation(),
    };
    const inputValues = JSON.parse(JSON.stringify(this.item.config.getInputs()));

    let newItemCopy = this.getModelItemConfigByTool(object, inputValues);
    newItemCopy.id = this.item.config.getId();

    this.item.config.assignWithSave(newItemCopy);
  }

  private getItemPositionByType(){
    if (this.item.config.getObjectConfig().getPosition()){
      return this.item.config.getObjectConfig().getPosition()
    }
    else {
      return this.item.config.getObjectConfig().getPositions();
    }
  }


  private getModelItemConfigByTool(object, inputValues?){

    let tool = object.tool,
        position = object.position,
        rotation = object.rotation;
    let fixedSize = {fixed_height: null, fixed_width: null, fixed_width_px: null};

    const getColor = (fieldName: string) => {
      const defColor = tool.default_color.toString().trim();
      const requiredColor = (tool[fieldName]) ? tool[fieldName].toString().trim() : '';
      return requiredColor.length > 0
        ? requiredColor
        : defColor.length > 0
          ? defColor
          : '#000000';
    };

    if (tool.params){
      if (tool.params.hasOwnProperty('fixedHeight')){
        fixedSize.fixed_height = tool.params['fixedHeight'].value;
      }
      if (tool.params.hasOwnProperty('fixedWidth')){
        fixedSize.fixed_width = tool.params['fixedWidth'].value;
      }
      if (tool.params.hasOwnProperty('fixedWidthPx')){
        fixedSize.fixed_width_px = tool.params['fixedWidthPx'].value;
      }
    }

    if (!(tool.params instanceof Object)){
      tool.params = {};
    }

    const inputs = JSON.parse(JSON.stringify(tool.params));
    if (inputValues) {
      for (const i in inputValues) {
        if (inputValues.hasOwnProperty(i) && !inputValues[i].hasOwnProperty('calculation')) {
          if (inputs.hasOwnProperty(i) && inputValues[i].hasOwnProperty('value')) {
            inputs[i].value = inputValues[i].value;
          } else {
            inputs[i] = inputValues[i];
          }
        }
      }
    }

    let _preparedItem = {

      id: null,
      group: tool.category.name,
      title: tool.name,
      originalTitle: tool.name,
      toolId: tool.id,
      copyOf: this.item.config.getCopyParentId,
      copyDirection: this.item.config.copyDirection,
      copyGapInMeters: this.item.config.copyGapInMeters,
      creator: window['PROFILE']['name'],
      creatorEmail: window['PROFILE']['email'],
      createdAt: Math.round(Date.now() / 1000),
      objects: [{
        area: null,
        icon: {},
        map_icon: tool.map_icon,
        latLngs: position,
        length: null,
        center: position,
        originalPosition: position,
        position: position,
        radius: position,
        rotation: rotation,
        tool_id: tool.id,
        tool_type: tool.type,
        fixed_height: fixedSize.fixed_height || tool.fixed_height_meters,
        fixed_width: fixedSize.fixed_width || tool.fixed_width_meters,
        fixed_width_px: fixedSize.fixed_width_px || (tool.params && tool.params['fixedWidthPx']) ? tool.params['fixedWidthPx'] : null,
        fillColor: getColor('fill_color'),
        fillOpacity: (tool.default_opacity.toString().trim().length > 0) ? tool.default_opacity : 1,
        strokeColor: getColor('line_color'),
        strokeWeight:  tool.hasOwnProperty('map_configs')
        && tool.map_configs.hasOwnProperty('cesium')
        && tool.map_configs.cesium.hasOwnProperty('line_width') ? tool.map_configs.cesium.line_width : tool.line_thickness || 0,
      }],
      inputs: inputs
    };
    return _preparedItem;
  }

  private processServerItemUpdate(config:ItemConfig){
    let newConfig = this.getObjectConfigFromPlugin(config);
    this.item.getSubject(ObjectArrayComponent.OBJECT_ARRAY_CONFIG_SUBJECT).next(newConfig);
  }

  public getCopiesFlatList(){
    let list: Item[] = this.im.getAllItems();
    let copies:Item[] = [];
    copies = list.filter(item => item.config.isCopy && this.currentParent && item.config.getCopyParentId == this.currentParent.getId() && item.getId() != this.currentParent.getId());
    copies.sort(function (a, b) { // sort by item ID
      return a.getId() - b.getId();
    });
    return copies;
  }

  public getCopies() {
    let list: Item[] = this.im.getAllItems();
    let copies:Item[] = [];
    copies = list.filter(item => item.config.isCopy && item.config.getCopyParentId == this.currentParent.getId());
    copies.sort(function (a, b) { // sort by item ID
      return a.getId().id - b.getId().id;
    });
    return {x: copies.filter(i => i.config.copyDirection === 'x'), y:copies.filter(i => i.config.copyDirection === 'y')}
  }

  private updateCopiesNotGroupedItems(actualCopies, config:ItemConfig){
    actualCopies.forEach((_item:Item)=>{
      _item.getSubject(Item.SUBJECT_ROTATE_TO).next(this.item.config.getRotation());
      if (this.item.config.getTool().params['fixedHeight'] || this.item.config.getTool().params['fixedWidth']){
        _item.config.getObjectConfig().setData('fixed_width', config.getObjectConfig().getFixedWidth());
        _item.config.getObjectConfig().setData('fixed_height', config.getObjectConfig().getFixedHeight());
        return;
      }
      let fixedWidthHeightInputs = {"fixedHeight": {value: config.getObjectConfig().getFixedHeight(), hidden: true}, "fixedWidth": {value: config.getObjectConfig().getFixedWidth(), hidden: true}}
      _item.config.assignInputs(fixedWidthHeightInputs, true);
    });
  }

  private updateCopiesGroupedItems(actualCopies, ...args){
    let babyInputs = this.item.config.getInputByName('__baby_number');
    actualCopies.forEach((i:Item)=>{
      i.config.assignInput('__baby_number', babyInputs)
    })
  }

  processInternalItemUpdate(config: ItemConfig){
    var actualCopies = this.getCopiesFlatList();
    let updateCopiesFieldsMethod = this.isCurrentParentContainChangedField() ? this.updateCopiesGroupedItems.bind(this): this.updateCopiesNotGroupedItems.bind(this);
    updateCopiesFieldsMethod(actualCopies, config);

    this.updateCopies();
  }

  updateCopies(newCopiesCount?: {x, y}) {
    if (this.isCurrentParentContainChangedField()) {
      let prevRotation = this.item.config.getObjectConfig().getPrevRotation();
      let rotation = this.item.config.getObjectConfig().getRotation();
      if (prevRotation != rotation){
        let _rotation = rotation - prevRotation;
        this.rotateGroupedItems(_rotation);
      }
      return;
    }
    let newCopiesCountX: number = (newCopiesCount) ? newCopiesCount.x : null;
    let newCopiesCountY: number = (newCopiesCount) ? newCopiesCount.y : null;
    if (newCopiesCount == null || undefined) {
      newCopiesCountX = this.currentParent.getCounterValue(ObjectArrayComponent.OBJECT_ARRAY_COUNTER_X) + 1;
      newCopiesCountY = this.currentParent.getCounterValue(ObjectArrayComponent.OBJECT_ARRAY_COUNTER_Y) + 1;
    }

    this.removeExtraItems(newCopiesCountX, newCopiesCountY);

    this.repositionCopies(newCopiesCountX, newCopiesCountY);
  }

  private removeExtraItems(newCopiesCountX, newCopiesCountY){
    var actualCopies = this.getCopies();
    var actualCopiesNumber = {x: actualCopies.x.length, y:actualCopies.y.length};
    //this.currentParent.getCounterValue(ObjectArrayComponent.OBJECT_ARRAY_COUNTER)
    newCopiesCountX = Math.max(1, newCopiesCountX); // min 1 copy. 1st copy is the item itself
    newCopiesCountY = Math.max(1, newCopiesCountY); // min 1 copy. 1st copy is the item itself

    let isAsked = false;
    let copies = this.currentParent.config.copies;
    let tmpCopies = JSON.parse(JSON.stringify(copies));
    if (newCopiesCountX < actualCopiesNumber.x + 1) {
      //settimeOutFor mouseUp after emptyClick
      isAsked = true;
      setTimeout(()=>{
        if (!confirm("Some objects may be deleted during this operation. Proceed?")) {
          return;
        }
        for (var i = newCopiesCountX; i < actualCopiesNumber.x+1; i++) {
          // let _copyItem:Item = actualCopies.x[i];
          // _copyItem.removeObject();

          copies.forEach((yArrayEl, index)=>{
            let copyItemId = yArrayEl[i];
            if (copyItemId){
              let copyItem = this.im.getItemById(copyItemId);
              delete tmpCopies[index][i]
              copyItem.removeObject();
            }
          })
        }
        tmpCopies = tmpCopies.map((row:any[])=>{
          return row.filter((e)=>{return e != null && e!='undefined'})
        })
        this.currentParent.config.copies = tmpCopies;
        return;
      }, 200);
    }

    if (newCopiesCountY < actualCopiesNumber.y + 1) { // user decreased number of copies
      //settimeOutFor mouseUp after emptyClick
      setTimeout(()=>{
        if (!isAsked){
          if (!confirm("Some objects may be deleted during this operation. Proceed?")) {
            return;
          }
        }
        // for (var i = newCopiesCountY-1; i < actualCopiesNumber.y; i++) {
        //   // let _copyItem:Item = actualCopies.y[i];
        //   // _copyItem.removeObject();
        // }

        for (let i = newCopiesCountY; i < actualCopiesNumber.y+1; i++) {
          copies[i].forEach((copyItemId, index)=>{
              if (copyItemId) {
                let copyItem = this.im.getItemById(copyItemId);
                delete tmpCopies[i][index];
                copyItem.removeObject();
              }
          })
        }
        tmpCopies = tmpCopies.map((row:any[])=>{
          return row.filter((colElm)=>{return colElm != null && colElm!='undefined'})
        }).filter(row => row.length > 0);
        this.currentParent.config.copies = tmpCopies
        return;
      }, 200);
    }

  }

  private repositionCopies(copiesCountX, copiesCountY){
    var gapMetersInputVal = this.item.config.getInputProp('__objectArrayGap', 'value');
    if (!gapMetersInputVal){
      gapMetersInputVal = JSON.stringify({x: 0, y:0});
    }
    var gapMeters;
    try {
      gapMeters = JSON.parse(gapMetersInputVal);
      for (let i in gapMeters){
        gapMeters[i] = parseFloat(gapMeters[i]);
      }
    }
    catch (e){
      return;
    }

    if (gapMeters.x<0){
      gapMeters.x = 0;
    }

    if (gapMeters.y<0){
      gapMeters.y = 0;
    }


    let copies = this.currentParent.config.copies;
    if (!copies){
      return;
    }
    const copiesXLength = copies && copies[0] ? copies[0].length : 0;
    this.repositionEmptyCopiesSpace(copies, {x: Math.max(copiesXLength , copiesCountX), y:Math.max(copies ? copies.length : 0, copiesCountY)}, gapMeters);

  }


  private rotateGroupedItems(rotation:number){
    let copies = this.getCopiesFlatList();
    if (!copies){
      return;
    }

    //offset in meters
    let additionalSceneRotation = this.cesiumAdapter.sceneRotatedDegrees;

    let x2= 0, y2 = 0;

    const mainItemWindowPosition:Cartesian2 = this.cesiumAdapter.getWindowCoordinatesFromMapPos(this.item.config.getObjectConfig().getPosition());
    for(let i = 0; i < copies.length; i++){
      let copy:Item = copies[i];
      let copyWindowPosition = this.cesiumAdapter.getWindowCoordinatesFromMapPos(copy.config.getObjectConfig().getPosition());
      let [x, y] = [copyWindowPosition.x - mainItemWindowPosition.x, copyWindowPosition.y - mainItemWindowPosition.y];

        x2 = x * Math.cos((rotation+additionalSceneRotation) * Math.PI/180) - y * Math.sin((rotation+additionalSceneRotation) * Math.PI/180);
        y2 = x * Math.sin((rotation+additionalSceneRotation) * Math.PI/180) + y * Math.cos((rotation+additionalSceneRotation) * Math.PI/180);

        let windowPosition =  {x: mainItemWindowPosition.x + x2, y: mainItemWindowPosition.y+y2};
        let movedCartesianPosition = this.cesiumAdapter.getPositionFromWindowCoordinates(windowPosition.x, windowPosition.y);
        let degreesPosition:MapPosition = this.cesiumAdapter.getDegreesFromCartesian(movedCartesianPosition);
        copy.config.getObjectConfig().setPosition(degreesPosition);
        copy.getSubject(Item.SUBJECT_ROTATE_BY).next({newVal:rotation, oldVal: copy.config.getObjectConfig().getRotation()});
        copy.config.getObjectConfig().setPrevRotation(rotation);
    }
    this.item.config.getObjectConfig().setPrevRotation(this.item.config.getObjectConfig().getRotation());
  }

  private repositionEmptyCopiesSpace(copies, copiesLength, gapMeters){

    let height = parseFloat(this.item.config.getObjectConfig().getFixedHeight()) || 2;
    let width = parseFloat(this.item.config.getObjectConfig().getFixedWidth()) || 2;
    let metersPerPx = this.cesiumAdapter.getMetersPerPx();
    let denominator = this.cesiumAdapter.getScaleDenominator(this.item.config.getObjectConfig().getPosition());
    //offset in meters
    let heightShift = height + gapMeters.y;
    let widthShift = width + gapMeters.x;
    let additionalSceneRotation = this.cesiumAdapter.sceneRotatedDegrees;

    let xLength = copiesLength.x;
    let yLength = copiesLength.y;

    let xm = 0, ym = 0, rotation = 0;
    let x2= 0, y2 = 0;

    if (this.item.config.getObjectConfig().getRotation()) {
      rotation = (this.item.config.getObjectConfig().getRotation());
    }


      let parentInputs = Object.assign({}, this.currentParent.config.getCopyOfInputs());
      if (parentInputs.hasOwnProperty('__objectArray')){
        delete parentInputs['__objectArray'];
      }

    if (yLength == 0 || xLength == 0){
      this.item.getObservable('ObjectArrayLoadingStatus').next(false);
    }

    for(let i = 0; i < yLength; i++){
      for (let j = 0; j < xLength; j++){

        x2 = j * widthShift * Math.cos((rotation-additionalSceneRotation) * Math.PI/180) - i*heightShift * Math.sin((rotation-additionalSceneRotation) * Math.PI/180);
        y2 = j *widthShift * Math.sin((rotation-additionalSceneRotation) * Math.PI/180) + i*heightShift * Math.cos((rotation-additionalSceneRotation) * Math.PI/180);


        // ym = heightShift * Math.cos(-(rotation - additionalSceneRotation) * Math.PI / 180) * (i+1);
        // xm = widthShift * Math.sin((90-rotation+additionalSceneRotation) * Math.PI / 180) * (j+1);

        // xm = xm/denominator;
        // ym = ym/denominator;

        x2 = x2/ denominator;
        y2 = y2/ denominator;
        //calculate position with meters offset
        let windowPosition = this.cesiumAdapter.calculateOffsetByMeters(this.item.config.getObjectConfig().getPosition(), x2, y2);
        let movedCartesianPosition = this.cesiumAdapter.getPositionFromWindowCoordinates(windowPosition.x, windowPosition.y);
        let degreesPosition:MapPosition = this.cesiumAdapter.getDegreesFromCartesian(movedCartesianPosition);


        if (!copies[i]){
          copies[i] = [];
        }

        if (!copies[i][j]){
            let positionInCopies = {x: j, y: i}
          let copyDirection = null;
            if(i == 0){
              copyDirection = 'x';
            }
            else if (j == 0){
              copyDirection = 'y';
            }
            else{
              copyDirection = 'z';
            }

            this.im.duplicateItemAsCopy(this.item, degreesPosition, additionalSceneRotation, copyDirection, positionInCopies).then((item:Item)=>{
              item.config.getObjectConfig().setPrevRotation(this.item.config.getObjectConfig().getRotation()-additionalSceneRotation);
              this.disableLoading(i,j,yLength, xLength);
          });
        }
        else{
          let duplicate = this.im.getItemById(copies[i][j]);
          if (duplicate && !(i==0 && j==0)) {
              duplicate.config.assignInputs(parentInputs, true);
              duplicate.config.getObjectConfig().setPosition(degreesPosition);
              duplicate.config.getObjectConfig().setPrevRotation(this.item.config.getObjectConfig().getRotation());
          }
          this.disableLoading(i,j,yLength, xLength);
        }
      }
    }
    this.item.config.getObjectConfig().setPrevRotation(this.item.config.getObjectConfig().getRotation());
  }

  private disableLoading(i,j,yLength, xLength){
    if (i == yLength-1 && j == xLength-1){
      timer(1000).subscribe(()=>{
        this.item.getObservable('ObjectArrayLoadingStatus').next(false);
      })
    }
  }

  private getObjectConfigFromPlugin(config: ItemConfig){
  return {
      copyDirection: config.copyDirection,
      copyGapInMeters: config.copyGapInMeters,
    };
  }

  private getRotatePoint(cx, cy, x, y, angle) {
    var radians = (Math.PI / 180) * angle,
      cos = Math.cos(radians),
      sin = Math.sin(radians),
      nx = (cos * (x - cx)) + (sin * (y - cy)) + cx,
      ny = (cos * (y - cy)) - (sin * (x - cx)) + cy;
    return [nx, ny];
  }

  /**
   * initialize listener that remove copyOf prop if parent doesn't exist after items have been initialized
   * @private
   */
  private initializeAdditionalChildListeners() {
    if (this.item.config.isCopy) {
      this.childSubscriptions.add(this.gs.itemsLoaded$.pipe(skipWhile(v => !v )).pipe(take(1)).subscribe(()=>{
        if (!this.im.getItemById(this.item.config.getCopyParentId)) {
          this.item.config.removeCopyOf();
          this.childSubscriptions.unsubscribe();
        }
      }))
    }
  }
}
